import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle2,
  History,
  RefreshCw,
  Terminal,
  XCircle,
  Clock,
  Filter,
  Search,
  SquarePen,
} from 'lucide-react';
import Pagination from '../../../Shared/Pagination/Pagination';
import { PaginationResponse } from '../../../Shared/Pagination/PaginationResponse';
import { useRedirect } from '../../../Shared/Hooks/useRedirect ';
import { storage } from '../../../Shared/Utils/authStorage';
import { getDataConnections, getQueryHistory } from '../../Services/DataService';
import { DataConnection, QueryHistoryItem } from '../../Types/DataServiceType';
import { CopyButton } from './DataResultGrid';
import { dataServiceBase } from './dataServiceRoutes';

const PAGE_SIZE = 15;

const ORIGIN_STYLES: Record<string, string> = {
  BUILDER: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-400',
  CONSOLE: 'border-violet-500/30 bg-violet-500/10 text-violet-400',
  SUGGESTION: 'border-slate-700 bg-slate-950 text-slate-400',
  ROW_MUTATION: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
};

const formatDateTime = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const HistoryComponent = () => {
  const redirect = useRedirect();
  const isSuperAdmin = (storage.getRole() || '').toUpperCase() === 'SUPER_ADMIN';

  const [history, setHistory] = useState<QueryHistoryItem[]>([]);
  const [pagination, setPagination] = useState<PaginationResponse<QueryHistoryItem> | null>(null);
  const [total, setTotal] = useState(0);
  const [connections, setConnections] = useState<DataConnection[]>([]);
  const [connectionId, setConnectionId] = useState('');
  const [sqlFilter, setSqlFilter] = useState('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState<string>('');

  // The console is the only surface that can re-run a raw statement, and it is
  // super-admin only, so the hand-off is offered under the same condition.
  const consoleBase = dataServiceBase();

  useEffect(() => {
    const load = async () => {
      try {
        const response = await getDataConnections({}, 0, 100);
        if (response.success && response.data) {
          setConnections(response.data.page?.content ?? []);
        }
      } catch (err) {
        console.error('Failed to load connections', err);
      }
    };
    load();
  }, []);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getQueryHistory(connectionId || undefined, page, PAGE_SIZE);
      if (response.success && response.data) {
        const pageData = response.data.page;
        setHistory(pageData.content ?? []);
        setPagination(pageData);
        setTotal(response.data.stats ?? 0);
        if (pageData.totalPages > 0 && page >= pageData.totalPages) {
          setPage(pageData.totalPages - 1);
        }
      } else {
        setHistory([]);
        setPagination(null);
        setError(response.message || 'Could not load the query history.');
      }
    } catch (err) {
      console.error('Failed to load history', err);
      setHistory([]);
      setPagination(null);
      setError('An unexpected error occurred while loading the history.');
    } finally {
      setLoading(false);
    }
  }, [connectionId, page]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  useEffect(() => {
    setPage(0);
  }, [connectionId]);

  // Filters the current page only: the history endpoint has no text search, and
  // searching across every past statement would mean a server-side change.
  const visibleHistory = history.filter((item) => {
    const needle = sqlFilter.trim().toLowerCase();
    if (!needle) return true;
    return (
      item.executedSql.toLowerCase().includes(needle) ||
      (item.tableName ?? '').toLowerCase().includes(needle) ||
      (item.origin ?? '').toLowerCase().includes(needle)
    );
  });

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
            <History size={22} />
          </span>
          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">Query History</h1>
            <p className="text-xs font-semibold text-slate-400 md:text-sm">
              Every statement the data service ran on your behalf
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={fetchHistory}
            disabled={loading}
            className="flex items-center justify-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-cyan-400 transition hover:border-cyan-500/40 disabled:opacity-50"
          >
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
          <div className="flex items-center justify-between">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
              <History size={16} />
            </span>
            <span className="text-2xl font-black text-slate-100">{total.toLocaleString()}</span>
          </div>
          <p className="mt-2 text-[11px] font-bold uppercase tracking-widest text-slate-400">
            Total statements
          </p>
        </div>
        <div className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
          <div className="flex items-center justify-between">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
              <CheckCircle2 size={16} />
            </span>
            <span className="text-2xl font-black text-slate-100">
              {history.filter((item) => item.success).length}
            </span>
          </div>
          <p className="mt-2 text-[11px] font-bold uppercase tracking-widest text-slate-400">
            Succeeded on this page
          </p>
        </div>
        <div className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
          <div className="flex items-center justify-between">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-violet-500/30 bg-violet-500/10 text-violet-400">
              <Terminal size={16} />
            </span>
            <span className="text-2xl font-black text-slate-100">
              {history.filter((item) => item.isRawSql).length}
            </span>
          </div>
          <p className="mt-2 text-[11px] font-bold uppercase tracking-widest text-slate-400">
            Raw console on this page
          </p>
        </div>
        <div className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/60 p-4">
          <div className="flex items-center justify-between">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-400">
              <Clock size={16} />
            </span>
            <span className="text-2xl font-black text-slate-100">
              {history.length > 0
                ? Math.round(
                    history.reduce((totalMs, item) => totalMs + item.durationMs, 0) / history.length
                  )
                : 0}
              <span className="text-sm">ms</span>
            </span>
          </div>
          <p className="mt-2 text-[11px] font-bold uppercase tracking-widest text-slate-400">
            Average duration
          </p>
        </div>
      </div>

      <div className="mb-6 grid gap-3 md:grid-cols-2">
        <div>
          <label className="mb-1.5 flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-slate-400">
            <Filter size={13} /> Connection
          </label>
          <select
            value={connectionId}
            onChange={(e) => setConnectionId(e.target.value)}
            className="w-full appearance-none rounded-xl border border-slate-800 bg-slate-950 px-3.5 py-2.5 text-sm text-slate-200 outline-none transition [color-scheme:dark] focus:border-cyan-500/50"
          >
            <option value="" className="bg-slate-900 text-slate-100">
              All connections
            </option>
            {connections.map((item) => (
              <option key={item.id} value={item.id} className="bg-slate-900 text-slate-100">
                {item.alias}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="mb-1.5 flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-slate-400">
            <Search size={13} /> Statement
          </label>
          <div className="relative">
            <Search
              size={15}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
            />
            <input
              type="text"
              value={sqlFilter}
              onChange={(e) => setSqlFilter(e.target.value)}
              placeholder="Search this page by SQL, table or origin…"
              className="w-full rounded-xl border border-slate-800 bg-slate-950 py-2.5 pl-9 pr-3 text-sm text-slate-200 placeholder-slate-600 outline-none transition focus:border-cyan-500/50"
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((idx) => (
            <div key={idx} className="h-16 animate-pulse rounded-2xl border border-slate-800 bg-slate-900/40" />
          ))}
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-rose-500/20 bg-rose-500/5 py-16 text-center text-sm font-bold text-rose-400">
          {error}
        </div>
      ) : visibleHistory.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-700 bg-slate-900/30 py-20 text-center">
          <History size={26} className="mx-auto mb-2 text-slate-600" />
          <p className="text-xs font-black uppercase tracking-widest text-slate-400">
            {sqlFilter.trim() ? 'No statement on this page matches' : 'No history yet'}
          </p>
          <p className="mt-1 text-[11px] font-semibold text-slate-500">
            {sqlFilter.trim()
              ? 'Clear the search, or turn the page to look through older statements.'
              : 'Statements appear here as soon as you run a query.'}
          </p>
        </div>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-2">
          {visibleHistory.map((item) => {
            const isOpen = expanded === item.id;
            return (
              <div
                key={item.id}
                className="rounded-2xl border border-slate-800 bg-slate-900/40 transition hover:border-slate-700"
              >
                <div className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <button
                    onClick={() => setExpanded(isOpen ? '' : item.id)}
                    className="flex min-w-0 flex-1 flex-wrap items-center gap-3 text-left"
                  >
                    <span
                      className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border ${
                        item.success
                          ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
                          : 'border-rose-500/30 bg-rose-500/10 text-rose-400'
                      }`}
                    >
                      {item.success ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
                    </span>

                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="text-xs font-black text-slate-200">
                          {item.tableName || 'raw statement'}
                        </span>
                        {item.origin && (
                          <span
                            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9px] font-black uppercase tracking-widest ${
                              ORIGIN_STYLES[item.origin] ?? 'border-slate-700 bg-slate-950 text-slate-400'
                            }`}
                          >
                            {item.origin}
                          </span>
                        )}
                        {item.isRawSql && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-violet-500/30 bg-violet-500/10 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest text-violet-400">
                            Raw
                          </span>
                        )}
                      </span>
                      <span className="mt-1 block truncate text-[11px] font-semibold text-slate-500">
                        {item.executedSql}
                      </span>
                    </span>

                    <span className="flex flex-shrink-0 items-center gap-4 text-[11px] font-bold text-slate-500">
                      <span>{item.rowCount.toLocaleString()} rows</span>
                      <span className="w-16 text-right">{item.durationMs} ms</span>
                      <span className="w-40 text-right">{formatDateTime(item.createdAt)}</span>
                    </span>
                  </button>

                  {/* Siblings of the toggle, not children: a button inside a button
                      is invalid and browsers disagree on what it activates. */}
                  <div className="flex flex-shrink-0 items-center gap-2">
                    <CopyButton text={item.executedSql} label="Copy SQL" />
                    {isSuperAdmin && (
                      <button
                        onClick={() =>
                          redirect(
                            `${consoleBase}/console?sql=${encodeURIComponent(item.executedSql)}&connectionId=${item.connectionId}`
                          )
                        }
                        title="Open this statement in the SQL console"
                        className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-950 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-violet-400 transition hover:border-violet-500/40"
                      >
                        <SquarePen size={11} /> Open
                      </button>
                    )}
                  </div>
                </div>

                {isOpen && (
                  <div className="border-t border-slate-800 px-4 py-3">
                    {item.errorMessage && (
                      <p className="mb-2 rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-[11px] font-bold text-rose-300">
                        {item.errorMessage}
                      </p>
                    )}
                    <div className="mb-1.5 flex items-center justify-between gap-2">
                      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                        Executed statement
                      </p>
                      <div className="flex items-center gap-2">
                        <CopyButton text={item.executedSql} label="Copy" />
                        {isSuperAdmin && (
                          <button
                            onClick={() =>
                              redirect(
                                `${consoleBase}/console?sql=${encodeURIComponent(item.executedSql)}&connectionId=${item.connectionId}`
                              )
                            }
                            className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-950 px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-violet-400 transition hover:border-violet-500/40"
                          >
                            <SquarePen size={11} /> Open in console
                          </button>
                        )}
                      </div>
                    </div>
                    <pre className="overflow-x-auto whitespace-pre-wrap break-words rounded-xl border border-slate-800 bg-slate-950 p-3 text-[11px] leading-relaxed text-slate-300">
                      {item.executedSql}
                    </pre>
                  </div>
                )}
              </div>
            );
          })}
        </motion.div>
      )}

      {pagination && pagination.totalElements > 0 && (
        <div className="mt-6 rounded-2xl border border-slate-800 bg-slate-900/40 px-4 py-2">
          <Pagination
            page={pagination.page}
            size={pagination.size}
            totalElements={pagination.totalElements}
            totalPages={pagination.totalPages}
            first={pagination.first}
            last={pagination.last}
            hasNext={pagination.hasNext}
            hasPrevious={pagination.hasPrevious}
            setPage={setPage}
          />
        </div>
      )}
    </div>
  );
};

export default HistoryComponent;
